import React, { useState } from "react";
import {
  Typography,
  Box,
  useTheme,
  alpha,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  IconButton,
  Divider,
  Rating,
  Paper,
  AvatarGroup,
  LinearProgress,
} from "@mui/material";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ForumIcon from "@mui/icons-material/Forum";
import PeopleIcon from "@mui/icons-material/People";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WhatshotIcon from "@mui/icons-material/Whatshot";
// import PushPinIcon from "@mui/icons-material/Pushpin";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ReplyIcon from "@mui/icons-material/Reply";
import AddIcon from "@mui/icons-material/Add";
import SupportLayout from "./SupportLayout";

const Community = () => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  const topics = [
    {
      id: 1,
      title: "Best practices for Airbnb turnover cleanings",
      content: "I've been hosting for 6 months and wanted to share what works for quick turnovers...",
      category: "Tips & Tricks",
      tags: ["Airbnb", "Turnover", "Efficiency"],
      author: {
        name: "Sarah Johnson",
        avatar: "S",
        role: "Super Host",
        joinDate: "2023"
      },
      stats: {
        replies: 24,
        views: 342,
        likes: 56
      },
      lastActivity: "2 hours ago",
      isHot: true,
      isPinned: true
    },
    {
      id: 2,
      title: "How to handle difficult cleaner situations?",
      content: "Looking for advice on handling a cleaner who consistently arrives late...",
      category: "Discussion",
      tags: ["Management", "Communication"],
      author: {
        name: "Mike Chen",
        avatar: "M",
        role: "Host",
        joinDate: "2024"
      },
      stats: {
        replies: 18,
        views: 245,
        likes: 32
      },
      lastActivity: "5 hours ago",
      isHot: true,
      isPinned: false
    },
    // Add more topics...
  ];

  const trendingTopics = [
    { title: "New pricing structure explained", replies: 45, views: 890 },
    { title: "Deep cleaning vs standard cleaning", replies: 32, views: 567 },
    { title: "How to get 5-star reviews consistently", replies: 28, views: 432 },
    { title: "Winter cleaning challenges", replies: 19, views: 298 }
  ];

  const topContributors = [
    { name: "Sarah Johnson", contributions: 156, avatar: "S", badge: "Expert" },
    { name: "Mike Chen", contributions: 98, avatar: "M", badge: "Rising Star" },
    { name: "Emily Rodriguez", contributions: 87, avatar: "E", badge: "Helper" },
    { name: "David Kim", contributions: 65, avatar: "D", badge: "Active" }
  ];

  return (
    <SupportLayout
      title="Host Community Forum"
      category="Community"
      subtitle="Connect with fellow hosts, share experiences, and get answers from the community"
      breadcrumbs={[
        { label: "Help Center", link: "/help" },
        { label: "Community" }
      ]}
      stats={[
        { value: "5k+", label: "Members" },
        { value: "1.2k", label: "Topics" },
        { value: "15k+", label: "Replies" },
        { value: "98%", label: "Response Rate" }
      ]}
    >
      {/* Welcome Banner */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={8}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Welcome to the Host Community! 👋
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Join 5,000+ hosts sharing tips, asking questions, and growing together.
              Introduce yourself and start connecting!
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              sx={{ borderRadius: 2 }}
            >
              Create New Topic
            </Button>
          </Grid>
          <Grid item xs={12} md={4}>
            <AvatarGroup max={4} sx={{ justifyContent: 'flex-end' }}>
              <Avatar>S</Avatar>
              <Avatar>M</Avatar>
              <Avatar>E</Avatar>
              <Avatar>D</Avatar>
              <Avatar>J</Avatar>
            </AvatarGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 1 }}>
              +5,000 members online now
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column - Topics */}
        <Grid item xs={12} md={8}>
          {/* Search and Filter Bar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              placeholder="Search topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="outlined" size="small">
              Filter
            </Button>
          </Box>

          {/* Tabs */}
          <Tabs 
            value={activeTab} 
            onChange={(e, val) => setActiveTab(val)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Latest Discussions" />
            <Tab label="Trending" />
            <Tab label="Unanswered" />
            <Tab label="My Posts" />
          </Tabs>

          {/* Topics List */}
          {topics.map((topic) => (
            <Card key={topic.id} sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {topic.isPinned && (
                      <Chip 
                        label="Pinned" 
                        size="small" 
                        icon={<WhatshotIcon />}
                        sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}
                      />
                    )}
                    {topic.isHot && (
                      <Chip 
                        label="Hot" 
                        size="small" 
                        icon={<WhatshotIcon />}
                        sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }}
                      />
                    )}
                    <Chip 
                      label={topic.category} 
                      size="small"
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {topic.lastActivity}
                  </Typography>
                </Box>

                <Typography 
                  variant="h6" 
                  component={Link} 
                  to={`/community/topic/${topic.id}`}
                  sx={{ 
                    textDecoration: 'none', 
                    color: 'inherit',
                    fontWeight: 600,
                    mb: 1,
                    display: 'block',
                    '&:hover': { color: theme.palette.primary.main }
                  }}
                >
                  {topic.title}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {topic.content}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {topic.tags.map((tag, idx) => (
                    <Chip key={idx} label={tag} size="small" variant="outlined" />
                  ))}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                      {topic.author.avatar}
                    </Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {topic.author.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {topic.author.role} • Member since {topic.author.joinDate}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption">{topic.stats.replies}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <VisibilityIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption">{topic.stats.views}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ThumbUpIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption">{topic.stats.likes}</Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
              <CardActions sx={{ pt: 0, pl: 2, pb: 2 }}>
                <Button size="small" startIcon={<ReplyIcon />}>
                  Reply
                </Button>
              </CardActions>
            </Card>
          ))}

          {/* Load More */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button variant="outlined">
              Load More Topics
            </Button>
          </Box>
        </Grid>

        {/* Right Column - Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Create Topic CTA */}
          <Button 
            variant="contained" 
            fullWidth 
            startIcon={<AddIcon />}
            sx={{ mb: 3, py: 1.5, borderRadius: 2 }}
          >
            Start New Discussion
          </Button>

          {/* Trending Topics */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon sx={{ color: theme.palette.primary.main }} />
              Trending Topics
            </Typography>
            {trendingTopics.map((topic, idx) => (
              <Box key={idx} sx={{ mb: 2 }}>
                <Typography 
                  variant="body2" 
                  component={Link}
                  to="#"
                  sx={{ 
                    textDecoration: 'none', 
                    color: 'inherit',
                    fontWeight: 500,
                    '&:hover': { color: theme.palette.primary.main }
                  }}
                >
                  {topic.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {topic.replies} replies
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {topic.views} views
                  </Typography>
                </Box>
                {idx < trendingTopics.length - 1 && <Divider sx={{ mt: 1 }} />}
              </Box>
            ))}
          </Paper>

          {/* Top Contributors */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon sx={{ color: theme.palette.primary.main }} />
              Top Contributors
            </Typography>
            {topContributors.map((contributor, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: theme.palette.primary.main }}>
                  {contributor.avatar}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {contributor.name}
                    </Typography>
                    <Chip label={contributor.badge} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {contributor.contributions} contributions
                  </Typography>
                </Box>
              </Box>
            ))}
          </Paper>

          {/* Community Guidelines */}
          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.02) }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              📋 Community Guidelines
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              • Be respectful and helpful<br />
              • No self-promotion or spam<br />
              • Keep discussions relevant<br />
              • Report inappropriate content<br />
            </Typography>
            <Button size="small" sx={{ mt: 1 }}>Read Full Guidelines →</Button>
          </Paper>
        </Grid>
      </Grid>
    </SupportLayout>
  );
};

export default Community;